"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useSession } from "@/lib/auth-react";
import { authFetch } from "@/utils/auth-fetch";
import { useSiteStatus } from "@/components/SiteStatusGate";
import { usePaymentAccess } from "@/hooks/usePaymentAccess";
import {
  Home,
  Calendar,
  Wallet,
  Settings,
  LogOut,
  Users,
  ChevronDown,
  User,
  Camera,
  Search,
  ShoppingBag,
  GraduationCap,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useLogout } from "@/hooks/useLogout";

// Navigation menu items for student dashboard.
// `title` is the stable English label — it drives the data-testid and the
// (not-yet-translated) mobile nav. `labelKey` selects the translated label
// from the `Nav` message namespace for the desktop sidebar.
export const studentMenuItems = [
  {
    title: "Home",
    labelKey: "home",
    url: "/dashboard",
    icon: Home,
  },
  {
    title: "Book a Session",
    labelKey: "bookSession",
    url: "/dashboard/book",
    icon: Search,
  },
  {
    title: "Bookings",
    labelKey: "bookings",
    url: "/dashboard/bookings",
    icon: Calendar,
  },
  {
    title: "Teachers",
    labelKey: "teachers",
    url: "/dashboard/teachers",
    icon: Users,
  },
  {
    title: "Credits",
    labelKey: "credits",
    url: "/dashboard/credits",
    icon: Wallet,
  },
  {
    title: "Buy credits",
    labelKey: "buyCredits",
    url: "/dashboard/buy-credits",
    icon: ShoppingBag,
  },
  {
    title: "Smart Teacher",
    labelKey: "smartTeacher",
    url: "/dashboard/smart-teacher",
    icon: GraduationCap,
  },
];

// Settings sub-items for student (no payout)
const settingsSubItems = [
  { title: "Profile", labelKey: "profile", url: "/dashboard/settings", icon: User },
  { title: "Profile Picture", labelKey: "profilePicture", url: "/dashboard/settings/profile-picture", icon: Camera },
];

// Student sidebar navigation component
export function StudentSidebar({ side }: { side?: "left" | "right" }) {
  const pathname = usePathname();
  const t = useTranslations("Nav");
  const { data: session } = useSession();
  const { handleLogout, isLoggingOut } = useLogout();
  const {
    payments: { enabled: paymentsEnabled },
  } = useSiteStatus();
  // No live purchase flow when payments are disabled — drop "Buy credits".
  // Test users can still pay, so keep it for them.
  const access = usePaymentAccess();
  const canBuy = access?.enabled ?? paymentsEnabled;
  const visibleMenuItems = canBuy
    ? studentMenuItems
    : studentMenuItems.filter((i) => i.url !== "/dashboard/buy-credits");
  const isSettingsPage = pathname.startsWith("/dashboard/settings");
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [upcomingCount, setUpcomingCount] = useState(0);

  useEffect(() => {
    if (!session?.accessToken) return;
    authFetch("/api/appointments/student", session.accessToken)
      .then((r) => r.json())
      .then((body) => {
        const now = new Date();
        const upcoming = (body.data ?? []).filter(
          (a: { status: string; endTime: string; rescheduledToId?: string | null }) =>
            (a.status === "NOT_STARTED" || a.status === "STARTED") &&
            !a.rescheduledToId &&
            new Date(a.endTime) > now,
        );
        setUpcomingCount(upcoming.length);
      })
      .catch(() => {});
  }, [session?.accessToken]);

  return (
    <Sidebar side={side} className="mt-auto hidden h-[calc(100vh-4.5rem-var(--app-banner-h,0px))] border-r-0 border-l-0 bg-[#27335D] lg:block [&>div]:bg-[#27335D]">
      <SidebarContent className="bg-[#27335D]">
        {/* Navigation Menu */}
        <SidebarGroup className="flex-1 p-0">
          <SidebarGroupContent className="py-4 ps-4">
            <SidebarMenu>
              {visibleMenuItems.map((item, index) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton className="rounded-none" asChild>
                    <a
                      href={item.url}
                      data-testid={`sidebarStudent${item.title.replace(/\s+/g, "")}Link`}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                        (
                          item.title === "Home"
                            ? pathname === item.url
                            : pathname === item.url ||
                              pathname.startsWith(item.url + "/")
                        )
                          ? "rounded-s-lg bg-white text-[#27335D]"
                          : "text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                      }`}
                    >
                      <div className="me-3 h-5 w-5">
                        <item.icon className="h-5 w-5" />
                      </div>
                      {t(item.labelKey)}
                      {item.title === "Bookings" && upcomingCount > 0 && (
                        <span className="ms-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1.5 text-[10px] font-bold text-[#27335D]">
                          {upcomingCount}
                        </span>
                      )}
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Settings and Logout Section */}
        <SidebarGroup className="p-0">
          <SidebarGroupContent className="border-t border-gray-400 py-4 ps-4">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton className="rounded-none" asChild>
                  <button
                    onClick={() => setSettingsOpen((o) => !o)}
                    data-testid="sidebarStudentSettingsLink"
                    className={`flex w-full cursor-pointer items-center px-6 py-3 text-sm font-medium transition-colors ${
                      isSettingsPage
                        ? "rounded-s-lg bg-white text-[#27335D]"
                        : "text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                    }`}
                  >
                    <div className="me-3 h-5 w-5">
                      <Settings className="h-5 w-5" />
                    </div>
                    <span className="flex-1 text-start">{t("settings")}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${settingsOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {settingsOpen &&
                settingsSubItems.map((sub) => (
                  <SidebarMenuItem key={sub.title}>
                    <SidebarMenuButton className="rounded-none" asChild>
                      <a
                        href={sub.url}
                        data-testid={`sidebarStudentSettings${sub.title.replace(/\s+/g, "")}Link`}
                        className={`flex items-center py-2 ps-14 pe-6 text-sm font-medium transition-colors ${
                          pathname === sub.url
                            ? "rounded-s-lg bg-white/80 text-[#27335D]"
                            : "text-gray-300 hover:rounded-s-lg hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        <div className="me-3 h-4 w-4">
                          <sub.icon className="h-4 w-4" />
                        </div>
                        {t(sub.labelKey)}
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}

              <SidebarMenuItem>
                <SidebarMenuButton className="rounded-none" asChild>
                  <button
                    onClick={() => handleLogout()}
                    disabled={isLoggingOut}
                    data-testid="sidebarStudentLogoutButton"
                    className={`flex w-full items-center px-6 py-3 text-sm font-medium transition-colors ${
                      isLoggingOut
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer text-white hover:rounded-s-lg hover:bg-white/10 hover:text-white/50"
                    }`}
                  >
                    <div className="me-3 h-5 w-5">
                      <LogOut className="h-5 w-5" />
                    </div>
                    {isLoggingOut ? t("loggingOut") : t("logout")}
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
